import * as React from "react";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FolderTree, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";
import { MigrationType, MigrationTypeLabels, nounFor } from "../../models/Statistics/MigrationType";
import { NOT_FOUND_RETRY_TYPES } from "../../models/OrgNotFoundRetry";
import ActionConfirmationModal, { ACTION_CONFIGS } from "@/components/common/ActionConfirmationModal";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

interface OrgNotFoundRetryViewProps {
  /** The organization being viewed. Scopes the count query so switching organizations never shows stale counts. */
  organizationId: string;
  disabled?: boolean;
}

const plural = (count: number, one: string, many = `${one}s`) =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`;

/**
 * Org-wide retry of Not Found files. Every type that has Not Found files starts selected; the operator unticks the
 * ones to leave alone, sees what will be queued, and confirms with a reason.
 */
export const OrgNotFoundRetryView: React.FC<OrgNotFoundRetryViewProps> = ({ organizationId, disabled }) => {
  const organizationsRepo = new OrganizationsRepository();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Unticked types rather than ticked ones, so every type with Not Found files is selected as soon as counts arrive.
  const [excluded, setExcluded] = useState<Set<MigrationType>>(new Set());

  const countsQuery = useQuery({
    queryKey: [QUERY_KEYS.ORG_NOT_FOUND_RETRY_COUNTS, organizationId],
    enabled: open && !disabled,
    queryFn: () => organizationsRepo.getNotFoundRetryCounts(),
    refetchOnWindowFocus: false,
  });

  const counts = useMemo(
    () => new Map((countsQuery.data ?? []).map((c) => [c.migrationType, c])),
    [countsQuery.data]
  );
  const present = NOT_FOUND_RETRY_TYPES.filter((type) => (counts.get(type)?.fileCount ?? 0) > 0);
  const selected = present.filter((type) => !excluded.has(type));
  const selectedFiles = selected.reduce((sum, type) => sum + (counts.get(type)?.fileCount ?? 0), 0);
  const selectedEntities = selected.reduce((sum, type) => sum + (counts.get(type)?.entityCount ?? 0), 0);
  const nothingToRetry = countsQuery.isSuccess && present.length === 0;

  const toggle = (type: MigrationType, checked: boolean) =>
    setExcluded((current) => {
      const next = new Set(current);
      if (checked) next.delete(type);
      else next.add(type);
      return next;
    });

  // Errors propagate to ActionConfirmationModal, which shows them and keeps the dialog open with the reason.
  const handleConfirm = async (reason: string) => {
    const queued = await organizationsRepo.retryNotFoundFiles(selected, reason);
    const files = queued.reduce((sum, c) => sum + c.fileCount, 0);
    const entities = queued.reduce((sum, c) => sum + c.entityCount, 0);
    toast.success(`Queued ${plural(files, "Not Found file")} across ${plural(entities, "entity", "entities")} for retry`, {
      description: "The files change to Not Found (Marked For Retry) in the background, one parent at a time.",
    });
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORG_NOT_FOUND_RETRY_COUNTS, organizationId] });
    setOpen(false);
  };

  return (
    <>
      <Button variant="outline" size="sm" className="gap-2" disabled={disabled} onClick={() => setOpen(true)}>
        <RefreshCw className="size-4" />
        Retry Not Found (org-wide)
      </Button>

      <Sheet
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setExcluded(new Set());
        }}
      >
        <SheetContent side="right" className="sm:max-w-xl w-full gap-0">
          <SheetHeader className="border-b">
            <SheetTitle className="flex items-center gap-2">
              <RefreshCw className="size-5" />
              Retry Not Found files org-wide
              {/* Clear of the sheet's own close button, which sits in the same corner. */}
              <span className="ml-auto mr-8 flex items-center gap-1">
                <DocsHelpLink to="how-to/retry-not-found-files" label="How to retry Not Found files" />
              </span>
            </SheetTitle>
            <SheetDescription>
              Every Not Found file of the selected types goes back into the upload queue, across the whole
              organization. A file still missing at its source goes straight back to Not Found.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-4">
            {countsQuery.isLoading && (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Counting Not Found files…
              </div>
            )}

            {countsQuery.isError && (
              <div className="flex flex-col items-center justify-center gap-3 py-12">
                <p className="text-sm text-muted-foreground">Failed to load the Not Found file counts.</p>
                <Button variant="outline" size="sm" onClick={() => countsQuery.refetch()}>
                  Retry
                </Button>
              </div>
            )}

            {nothingToRetry && (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                <FolderTree className="size-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">The organization has no Not Found files.</p>
              </div>
            )}

            {countsQuery.isSuccess && !nothingToRetry && (
              <div className="flex flex-col divide-y rounded-md border">
                {NOT_FOUND_RETRY_TYPES.map((type) => {
                  const count = counts.get(type);
                  const files = count?.fileCount ?? 0;
                  const entities = count?.entityCount ?? 0;
                  const id = `not-found-retry-${type}`;
                  return (
                    <div key={type} className={cn("flex items-center gap-3 px-3 py-2.5", files === 0 && "opacity-50")}>
                      <Checkbox
                        id={id}
                        checked={files > 0 && !excluded.has(type)}
                        disabled={files === 0 || confirming}
                        onCheckedChange={(checked) => toggle(type, checked === true)}
                      />
                      <Label htmlFor={id} className="flex-1 cursor-pointer">
                        {MigrationTypeLabels[type]}
                      </Label>
                      <span className="text-sm text-muted-foreground tabular-nums">
                        {files === 0
                          ? "No Not Found files"
                          : `${plural(files, "file")} across ${entities.toLocaleString()} ${nounFor(type, entities)}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <SheetFooter className="border-t">
            <ActionConfirmationModal
              actionConfig={ACTION_CONFIGS.MARK_NOT_FOUND_FILES_FOR_RETRY_ORG_WIDE}
              onConfirm={handleConfirm}
              open={confirming}
              onClose={() => setConfirming(false)}
              description={
                <p className="text-sm">
                  {plural(selectedFiles, "Not Found file")} across {plural(selectedEntities, "entity", "entities")} (
                  {selected.map((type) => MigrationTypeLabels[type]).join(", ")}) will be queued for retry, whichever
                  client discovered them.
                </p>
              }
              warnings={["A file still missing at its source goes straight back to Not Found. Fix the source first."]}
            />
            <Button
              className="w-full gap-2"
              disabled={selected.length === 0 || !countsQuery.isSuccess}
              onClick={() => setConfirming(true)}
            >
              <RefreshCw className="size-4 shrink-0" />
              {!countsQuery.isSuccess
                ? "Counting Not Found files…"
                : selected.length === 0
                ? "Select at least one type"
                : `Retry ${plural(selectedFiles, "file")} across ${plural(selectedEntities, "entity", "entities")}`}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default OrgNotFoundRetryView;
